/** Clock in and out, and edit the day's attendance entries. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "@primereact/ui/dialog";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { DateInput } from "../../components/ui/controls.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { Tag } from "@primereact/ui/tag";
import { Textarea } from "@primereact/ui/textarea";
import { apiDelete, apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { useMe } from "../../lib/auth.js";
import { ConfirmButton, ErrorPanel, LoadingPanel } from "../shared.js";
import type { TimesheetData } from "./types.js";
import {
  dateOnly,
  duration,
  localTime,
  monday,
  timeInputValue,
  type TimeEntry,
  Metric,
  TimeMutationMessage,
} from "./time-common.js";

export function TimeClockPage() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const me = useMe();
  const today = dateOnly(new Date());
  const [params] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(
    () => params.get("date") ?? today,
  );
  const [entryDialog, setEntryDialog] = useState<TimeEntry | null>();
  const [now, setNow] = useState(() => Date.now());
  const week = monday(new Date(`${selectedDate}T12:00:00`));
  const year = Number(selectedDate.slice(0, 4));
  const query = useQuery({
    queryKey: ["time-week", week],
    queryFn: () =>
      apiGet<{ data: TimesheetData }>("/time/week", {
        query: { week },
      }).then((response) => response.data),
  });
  const balance = useQuery({
    queryKey: ["employee-balances", me.data?.user.id, year],
    queryFn: () =>
      apiGet<{
        data: { compensationRemainingMinutes: number };
      }>(`/employees/${me.data!.user.id}/balances/${year}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(me.data?.user.id),
  });
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  const sheet = query.data;
  const editable = sheet ? ["draft", "rejected"].includes(sheet.status) : false;
  const attendance = (sheet?.entries ?? []).filter(
    (entry) => entry.kind === "attendance" && entry.date === selectedDate,
  );
  const openEntry =
    selectedDate === today
      ? attendance.find((entry) => !entry.endAt)
      : undefined;
  const stampedMinutes = attendance.reduce((sum, entry) => {
    if (entry.endAt || !entry.startAt) return sum + entry.minutes;
    return (
      sum + Math.max(0, Math.floor((now - Date.parse(entry.startAt)) / 60_000))
    );
  }, 0);
  const plannedMinutes =
    sheet?.days.find((day) => day.date === selectedDate)?.plannedMinutes ?? 0;
  const difference = stampedMinutes - plannedMinutes;
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["time-week"] }),
      client.invalidateQueries({ queryKey: ["employee-balances"] }),
    ]);
  };
  const stamp = useMutation({
    mutationFn: (action: "clock_in" | "clock_out") =>
      apiPost("/time/stamp", { action }),
    onSuccess: refresh,
  });
  const removeEntry = useMutation({
    mutationFn: (id: string) => apiDelete(`/time/entries/${id}`),
    onSuccess: refresh,
  });
  const moveSelectedDate = (days: number) => {
    const date = new Date(`${selectedDate}T12:00:00`);
    date.setDate(date.getDate() + days);
    setSelectedDate(dateOnly(date));
  };
  return (
    <>
      <PageHeader
        title={t("time.clock")}
        subtitle={t("time.clockDescription")}
        actions={
          <Button
            type="button"
            onClick={() => setEntryDialog(null)}
            disabled={!editable}
          >
            <i className="pi pi-plus" />
            {t("time.addManualEntry", { defaultValue: "Add manual entry" })}
          </Button>
        }
      />
      <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl bg-app-surface p-3 shadow-sm ring-1 ring-app-border">
        <Button
          type="button"
          iconOnly
          variant="outlined"
          severity="secondary"
          aria-label={t("common.previous", { defaultValue: "Previous" })}
          onClick={() => moveSelectedDate(-1)}
        >
          <i className="pi pi-chevron-left" />
        </Button>
        <DateInput
          value={selectedDate}
          onChange={(value) => {
            if (value) setSelectedDate(value);
          }}
          className="w-44"
        />
        <Button
          type="button"
          variant="outlined"
          severity="secondary"
          onClick={() => setSelectedDate(today)}
        >
          {t("common.today", { defaultValue: "Today" })}
        </Button>
        <Button
          type="button"
          iconOnly
          variant="outlined"
          severity="secondary"
          aria-label={t("common.next", { defaultValue: "Next" })}
          onClick={() => moveSelectedDate(1)}
        >
          <i className="pi pi-chevron-right" />
        </Button>
      </div>
      {query.isLoading ? (
        <LoadingPanel />
      ) : query.error || !sheet ? (
        <ErrorPanel error={query.error} />
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(18rem,1fr)]">
            <div className="rounded-2xl bg-app-surface p-6 shadow-sm ring-1 ring-app-border sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-app-muted">
                    {selectedDate === today
                      ? openEntry
                        ? t("time.workflow.clockedIn")
                        : t("time.workflow.clockedOut")
                      : selectedDate}
                  </p>
                  <p className="mt-2 text-5xl font-semibold tabular-nums text-app-text">
                    {duration(stampedMinutes)}
                  </p>
                  <p className="mt-2 text-sm text-app-muted">
                    {openEntry?.startAt
                      ? t("time.clockedInSince", {
                          time: localTime(openEntry.startAt),
                        })
                      : selectedDate === today
                        ? t("time.noOpenSession")
                        : t("time.manualEntryHint", {
                            defaultValue:
                              "Select an entry below to edit its date or times.",
                          })}
                  </p>
                </div>
                <Tag
                  severity={
                    sheet.status === "approved"
                      ? "success"
                      : sheet.status === "rejected"
                        ? "danger"
                        : sheet.status === "submitted"
                          ? "info"
                          : "warn"
                  }
                >
                  {t(`time.status.${sheet.status}`)}
                </Tag>
              </div>
              {selectedDate === today ? (
                <Button
                  className="mt-8 w-full sm:w-auto"
                  size="large"
                  disabled={!editable}
                  loading={stamp.isPending || undefined}
                  onClick={() =>
                    stamp.mutate(openEntry ? "clock_out" : "clock_in")
                  }
                >
                  <i
                    className={
                      openEntry ? "pi pi-stop-circle" : "pi pi-play-circle"
                    }
                  />
                  {openEntry ? t("time.clockOut") : t("time.clockIn")}
                </Button>
              ) : null}
              <TimeMutationMessage error={stamp.error} />
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
              <Metric
                label={t("time.dayRecorded", {
                  defaultValue: "Recorded for selected day",
                })}
                value={duration(stampedMinutes)}
              />
              <Metric
                label={t("time.dailyGoalDifference")}
                value={duration(difference)}
              />
              <Metric
                label={t("time.compensationBalance", {
                  defaultValue: "Compensation balance",
                })}
                value={
                  balance.isLoading
                    ? "…"
                    : duration(balance.data?.compensationRemainingMinutes ?? 0)
                }
              />
            </div>
          </div>
          <div className="rounded-2xl bg-app-surface p-5 shadow-sm ring-1 ring-app-border">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-app-text">
                {t("time.clockSessions", { defaultValue: "Clock entries" })}
              </h2>
              <span className="text-sm text-app-muted">
                {t("time.dailyGoal", { value: duration(plannedMinutes) })}
              </span>
            </div>
            {attendance.length ? (
              <div className="divide-y divide-app-border">
                {attendance.map((entry, index) => (
                  <div
                    key={entry.id}
                    className="grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-3 py-3"
                  >
                    <span className="text-sm font-semibold text-app-muted">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium tabular-nums text-app-text">
                        {localTime(entry.startAt)} –{" "}
                        {entry.endAt
                          ? localTime(entry.endAt)
                          : t("time.inProgress")}
                      </p>
                      {entry.notes ? (
                        <p className="text-sm text-app-muted">{entry.notes}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag severity={entry.endAt ? "secondary" : "success"}>
                        {duration(
                          entry.endAt
                            ? entry.minutes
                            : Math.max(
                                0,
                                Math.floor(
                                  (now - Date.parse(entry.startAt!)) / 60_000,
                                ),
                              ),
                        )}
                      </Tag>
                      {editable && entry.endAt ? (
                        <Button
                          type="button"
                          iconOnly
                          variant="outlined"
                          severity="secondary"
                          aria-label={t("common.edit")}
                          onClick={() => setEntryDialog(entry)}
                        >
                          <i className="pi pi-pencil" />
                        </Button>
                      ) : null}
                      {editable ? (
                        <ConfirmButton
                          onConfirm={() => removeEntry.mutate(entry.id)}
                          confirmMessage={t("time.deleteEntryConfirm")}
                          loading={removeEntry.isPending || undefined}
                        >
                          <i className="pi pi-trash" />
                        </ConfirmButton>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-5 text-center text-sm text-app-muted">
                {t("time.noSessionsToday", {
                  defaultValue: "No clock entries for this day.",
                })}
              </p>
            )}
            <TimeMutationMessage error={removeEntry.error} />
          </div>
        </div>
      )}
      <Dialog.Root
        open={entryDialog !== undefined}
        modal
        dismissable={false}
        closeOnEscape={false}
        style={{ width: "min(38rem, calc(100vw - 2rem))" }}
      >
        <Dialog.Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Popup>
              <Dialog.Header>
                <Dialog.Title>
                  {entryDialog
                    ? t("time.editClockEntry", {
                        defaultValue: "Edit clock entry",
                      })
                    : t("time.addManualEntry", {
                        defaultValue: "Add manual entry",
                      })}
                </Dialog.Title>
                <Dialog.HeaderActions>
                  <Button
                    type="button"
                    iconOnly
                    variant="text"
                    severity="secondary"
                    aria-label={t("common.close")}
                    onClick={() => setEntryDialog(undefined)}
                  >
                    <i className="pi pi-times" />
                  </Button>
                </Dialog.HeaderActions>
              </Dialog.Header>
              <Dialog.Content>
                <ClockEntryForm
                  key={entryDialog?.id ?? selectedDate}
                  date={selectedDate}
                  entry={entryDialog ?? null}
                  onDone={async (savedDate) => {
                    setSelectedDate(savedDate);
                    setEntryDialog(undefined);
                    await refresh();
                  }}
                  onCancel={() => setEntryDialog(undefined)}
                />
              </Dialog.Content>
            </Dialog.Popup>
          </Dialog.Positioner>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function ClockEntryForm({
  date: initialDate,
  entry,
  onDone,
  onCancel,
}: {
  date: string;
  entry: TimeEntry | null;
  onDone: (date: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState(entry?.date ?? initialDate);
  const [startTime, setStartTime] = useState(
    timeInputValue(entry?.startAt ?? null),
  );
  const [endTime, setEndTime] = useState(timeInputValue(entry?.endAt ?? null));
  const [notes, setNotes] = useState(entry?.notes ?? "");
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        date,
        kind: "attendance",
        startTime,
        endTime,
        minutes: null,
        notes: notes.trim() || null,
      };
      return entry
        ? apiPatch(`/time/entries/${entry.id}`, {
            ...payload,
            version: entry.version,
          })
        : apiPost("/time/entries", payload);
    },
    onSuccess: () => onDone(date),
  });
  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Field className="sm:col-span-2" label={t("common.date")} required>
        <Input
          type="date"
          value={date}
          max={dateOnly(new Date())}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDate(event.target.value)
          }
          fluid
          required
        />
      </Field>
      <Field label={t("time.start")} required>
        <Input
          type="time"
          value={startTime}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setStartTime(event.target.value)
          }
          fluid
          required
        />
      </Field>
      <Field label={t("time.end")} required>
        <Input
          type="time"
          value={endTime}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setEndTime(event.target.value)
          }
          fluid
          required
        />
      </Field>
      <Field className="sm:col-span-2" label={t("common.notes")}>
        <Textarea
          value={notes}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setNotes(event.target.value)
          }
          rows={3}
          fluid
        />
      </Field>
      <TimeMutationMessage error={save.error} />
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button
          type="button"
          variant="outlined"
          severity="secondary"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={save.isPending || undefined}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
